import type {
  AuditEventDTO,
  BoardAlertLevel,
  BoardDTO,
  BoardSummaryDTO,
  DashboardDTO,
  GamePlayDTO,
  GroupDetailDTO,
  GroupStatsDTO,
  GroupSummaryDTO,
  InviteDTO,
  MeDTO,
  NotificationDTO,
  PastBoardsDTO,
  ReminderDTO,
  ScheduleDTO,
  SupportSummaryDTO,
  TemplateDTO,
  UpcomingGamesDTO,
  WatchBoardDTO,
} from "@gridaction/core";
import { infiniteQueryOptions, QueryClient, queryOptions } from "@tanstack/react-query";
import { ApiError, api } from "./api";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (failureCount, error) =>
        !(error instanceof ApiError && error.status >= 400 && error.status < 500) &&
        failureCount < 2,
      refetchOnWindowFocus: true,
    },
    mutations: { retry: false },
  },
});

export const keys = {
  me: ["me"] as const,
  dashboard: ["dashboard"] as const,
  // Under the dashboard's key, so whatever refreshes the dashboard refreshes this list too.
  past: (groupId: string | null) => ["dashboard", "past", groupId ?? "all"] as const,
  groups: ["groups"] as const,
  group: (id: string) => ["group", id] as const,
  groupBoards: (id: string) => ["group", id, "boards"] as const,
  invites: (id: string) => ["group", id, "invites"] as const,
  templates: (id: string) => ["group", id, "templates"] as const,
  activity: (groupId: string, boardId?: string) => ["activity", groupId, boardId ?? "all"] as const,
  stats: (groupId: string, season?: number) =>
    ["group", groupId, "stats", season ?? "latest"] as const,
  board: (id: string) => ["board", id] as const,
  boardAlerts: (id: string) => ["board", id, "alerts"] as const,
  games: (season?: number, seasonType?: number, week?: number) =>
    ["games", season ?? "current", seasonType ?? "", week ?? ""] as const,
  upcomingGames: ["games", "upcoming"] as const,
  gamePlays: (gameId: string) => ["games", gameId, "plays"] as const,
  notifications: ["notifications"] as const,
  adminOverview: ["admin", "overview"] as const,
  adminUsers: (search: string, page: number) => ["admin", "users", search, page] as const,
  adminUser: (id: string) => ["admin", "user", id] as const,
  adminSupport: (filter: string) => ["admin", "support", "list", filter] as const,
  adminTicket: (id: string) => ["admin", "support", "ticket", id] as const,
  adminSupportSummary: ["admin", "support", "summary"] as const,
  referral: ["referral"] as const,
  reminders: ["reminders"] as const,
};

/** The signed-in account, or null when signed out. */
export const meQuery = queryOptions({
  queryKey: keys.me,
  queryFn: async () => {
    try {
      return await api.get<MeDTO>("/me");
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  },
  staleTime: 60_000,
});

export const dashboardQuery = queryOptions({
  queryKey: keys.dashboard,
  queryFn: () => api.get<DashboardDTO>("/dashboard"),
  refetchInterval: 20_000,
});

export const groupsQuery = queryOptions({
  queryKey: keys.groups,
  queryFn: () => api.get<GroupSummaryDTO[]>("/groups"),
});

export const groupQuery = (id: string) =>
  queryOptions({
    queryKey: keys.group(id),
    queryFn: () => api.get<GroupDetailDTO>(`/groups/${id}`),
  });

/** Boards whose games are over, a page at a time, newest first. Optionally one group's. */
export const pastBoardsQuery = (groupId: string | null) =>
  infiniteQueryOptions({
    queryKey: keys.past(groupId),
    queryFn: ({ pageParam }) =>
      api.get<PastBoardsDTO>(
        `/boards/past?offset=${pageParam}${groupId ? `&group=${encodeURIComponent(groupId)}` : ""}`,
      ),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextOffset ?? undefined,
  });

export const groupBoardsQuery = (id: string) =>
  queryOptions({
    queryKey: keys.groupBoards(id),
    queryFn: () => api.get<BoardSummaryDTO[]>(`/groups/${id}/boards`),
  });

export const invitesQuery = (id: string) =>
  queryOptions({
    queryKey: keys.invites(id),
    queryFn: () => api.get<InviteDTO[]>(`/groups/${id}/invites`),
  });

export const templatesQuery = (id: string) =>
  queryOptions({
    queryKey: keys.templates(id),
    queryFn: () => api.get<TemplateDTO[]>(`/groups/${id}/templates`),
  });

export const activityQuery = (groupId: string, boardId?: string) =>
  queryOptions({
    queryKey: keys.activity(groupId, boardId),
    queryFn: () =>
      api.get<AuditEventDTO[]>(
        boardId ? `/boards/${boardId}/activity` : `/groups/${groupId}/activity`,
      ),
  });

export const statsQuery = (groupId: string, season?: number) =>
  queryOptions({
    queryKey: keys.stats(groupId, season),
    queryFn: () =>
      api.get<GroupStatsDTO>(`/groups/${groupId}/stats${season ? `?season=${season}` : ""}`),
  });

const LIVE_GAME = new Set(["in_progress", "halftime", "end_period", "delayed"]);

export const boardQuery = (id: string) =>
  queryOptions({
    queryKey: keys.board(id),
    queryFn: () => api.get<BoardDTO>(`/boards/${id}`),
    // Poll fast while picks or scoring are happening; ETags keep unchanged polls nearly free.
    refetchInterval: (query) => {
      const board = query.state.data;
      if (!board) return false;
      if (board.score.gameStatus && LIVE_GAME.has(board.score.gameStatus)) return 5_000;
      if (board.status === "final") return 60_000;
      return 8_000;
    },
  });

/** A shared board, as people holding its link see it: they follow along live, and the server answers a repeat check with a 304. */
export const watchQuery = (code: string) =>
  queryOptions({
    queryKey: ["watch", code],
    queryFn: () => api.get<WatchBoardDTO>(`/watch/${encodeURIComponent(code)}`),
    refetchInterval: 20_000,
    retry: false,
  });

export const gamesQuery = (season?: number, seasonType?: number, week?: number) =>
  queryOptions({
    queryKey: keys.games(season, seasonType, week),
    queryFn: () => {
      const params =
        season && seasonType && week
          ? `?season=${season}&seasonType=${seasonType}&week=${week}`
          : "";
      return api.get<ScheduleDTO>(`/games${params}`);
    },
    refetchInterval: (query) =>
      query.state.data?.games.some((game) => LIVE_GAME.has(game.status)) ? 20_000 : 5 * 60_000,
  });

/** A game's scoring plays so far. Enabled only while a square dialog actually needs them. */
export const gamePlaysQuery = (gameId: string) =>
  queryOptions({
    queryKey: keys.gamePlays(gameId),
    queryFn: () => api.get<GamePlayDTO[]>(`/games/${gameId}/plays`),
    staleTime: 15_000,
    refetchInterval: 15_000,
  });

/** The games a new grid can start on, and the one it starts on (the next prime-time game). */
export const upcomingGamesQuery = queryOptions({
  queryKey: keys.upcomingGames,
  queryFn: () => api.get<UpcomingGamesDTO>("/games/upcoming"),
  staleTime: 60_000,
  refetchInterval: 5 * 60_000,
});

export const notificationsQuery = queryOptions({
  queryKey: keys.notifications,
  queryFn: () => api.get<NotificationDTO[]>("/notifications"),
});

export const boardAlertsQuery = (id: string) =>
  queryOptions({
    queryKey: keys.boardAlerts(id),
    queryFn: () => api.get<{ level: BoardAlertLevel }>(`/boards/${id}/alerts`),
  });

export const remindersQuery = queryOptions({
  queryKey: keys.reminders,
  queryFn: () => api.get<ReminderDTO[]>("/reminders"),
});

/** Counts for the Support badge in the menus; staff only, refreshed every minute. */
export const supportSummaryQuery = queryOptions({
  queryKey: keys.adminSupportSummary,
  queryFn: () => api.get<SupportSummaryDTO>("/admin/support/summary"),
  refetchInterval: 60_000,
});
